import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaUser, FaEnvelope, FaLock, FaUserTag } from 'react-icons/fa';
import Button from '../common/Button';
import { Input } from '../common/Input';
import { User } from '../../types/user';
import { usersService } from '../../services/supabase/users.service';
import { useNotifications } from '../../context/NotificationContext';
import { useUserFilter } from '../../context/UserFilterContext';
import { useAuth } from '../../context/AuthContext';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1rem;
`;

const StyledInput = styled(Input)<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid ${({ theme, $hasError }) => 
    $hasError ? theme.colors.danger : theme.colors.border};
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    border-color: ${({ theme, $hasError }) => 
      $hasError ? theme.colors.danger : theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme, $hasError }) => 
      $hasError ? theme.colors.dangerLight : theme.colors.primaryLight};
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.danger};
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

const SuccessMessage = styled.div`
  color: ${({ theme }) => theme.colors.success};
  font-size: 0.85rem;
  margin-top: 0.25rem;
  padding: 0.5rem;
  background-color: ${({ theme }) => `${theme.colors.success}15`};
  border-radius: 4px;
  text-align: center;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
`;

const FormTitle = styled.h2`
  margin-bottom: 1rem;
`;

interface UserFormProps {
  user?: User;
  onCancel: () => void;
  onSuccess: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ user, onCancel, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    firstName: '',
    role: 'user' // Valeur par défaut: 'user'
  });
  
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
    name?: string;
    firstName?: string;
  }>({});
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { addNotification } = useNotifications();
  const { setSelectedUserId } = useUserFilter();
  const { supabaseUser } = useAuth();
  
  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        password: '',
        confirmPassword: '',
        name: user.name || '',
        firstName: user.first_name || '',
        role: user.role || 'user'
      });
    }
  }, [user]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const validateForm = (): boolean => {
    const errors: {
      email?: string;
      password?: string;
      confirmPassword?: string;
      name?: string;
      firstName?: string;
    } = {};
    
    // Validation de l'email
    if (!formData.email) {
      errors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "L'email n'est pas valide";
    }
    
    // Validation du mot de passe (uniquement pour les nouveaux utilisateurs)
    if (!user) {
      if (!formData.password) {
        errors.password = "Le mot de passe est requis";
      } else if (formData.password.length < 6) {
        errors.password = "Le mot de passe doit contenir au moins 6 caractères";
      }
      
      if (!formData.confirmPassword) {
        errors.confirmPassword = "La confirmation du mot de passe est requise";
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = "Les mots de passe ne correspondent pas";
        // Ajouter également un message d'erreur sur le champ password pour plus de visibilité
        if (!errors.password) {
          errors.password = "Les mots de passe ne correspondent pas";
        }
      }
    }
    
    // Validation du nom
    if (!formData.name) {
      errors.name = "Le nom est requis";
    }
    
    // Validation du prénom
    if (!formData.firstName) {
      errors.firstName = "Le prénom est requis";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Réinitialiser les erreurs
    setValidationErrors({});
    
    // Valider le formulaire et afficher les erreurs
    if (!validateForm()) {
      // Afficher une notification pour indiquer à l'utilisateur de corriger les erreurs
      addNotification({
        type: 'warning',
        title: 'Erreur de validation',
        message: 'Veuillez corriger les erreurs dans le formulaire'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      if (user) {
        // Mise à jour d'un utilisateur existant
        const { error } = await usersService.updateUser(user.id, {
          name: formData.name,
          first_name: formData.firstName,
          role: formData.role
        });
        
        if (error) {
          throw error;
        }
        
        addNotification({
          type: 'info',
          title: 'Succès',
          message: 'Utilisateur mis à jour avec succès'
        });
      } else {
        // Création d'un nouvel utilisateur
        try {
          const { data, error } = await usersService.createUser({
            email: formData.email,
            password: formData.password,
            name: formData.name,
            first_name: formData.firstName,
            role: formData.role
          });
          
          if (error) {
            // Gérer les erreurs spécifiques à Supabase
            if (error.message?.includes('User not allowed')) {
              throw new Error('Votre compte n\'a pas les permissions nécessaires pour créer des utilisateurs. Veuillez contacter l\'administrateur.');
            } else if (error.message?.includes('already registered')) {
              throw new Error('Un utilisateur avec cette adresse email existe déjà.');
            } else {
              throw error;
            }
          }
          
          if (!data) {
            throw new Error('Erreur lors de la création de l\'utilisateur.');
          }
        } catch (createError: any) {
          throw createError;
        }
        
        addNotification({
          type: 'info',
          title: 'Succès',
          message: 'Utilisateur créé avec succès'
        });
        
        // Forcer le retour à l'espace de l'admin après la création
        if (supabaseUser?.id) {
          setSelectedUserId(supabaseUser.id);
        }
      }
      
      setSuccess(true);
      
      // Réinitialiser le formulaire après un court délai
      setTimeout(() => {
        onSuccess();
      }, 1500);
      
    } catch (error: any) {
      console.error('Erreur lors de la gestion de l\'utilisateur:', error);
      
      // Message d'erreur plus convivial
      let errorMessage = 'Une erreur est survenue';
      
      if (error.message) {
        if (error.message.includes('User not allowed')) {
          errorMessage = 'Votre compte n\'a pas les permissions nécessaires pour créer des utilisateurs';
        } else if (error.message.includes('already registered')) {
          errorMessage = 'Un utilisateur avec cette adresse email existe déjà';
        } else if (error.message.includes('Password should be')) {
          errorMessage = 'Le mot de passe doit contenir au moins 6 caractères';
        } else {
          errorMessage = error.message;
        }
      }
      
      addNotification({
        type: 'warning',
        title: 'Erreur',
        message: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <FormContainer onSubmit={handleSubmit}>
      <FormTitle>{user ? 'Modifier l\'utilisateur' : 'Ajouter un utilisateur'}</FormTitle>
      
      <FormGroup>
        <Label htmlFor="email">Email</Label>
        <InputWrapper>
          <IconWrapper>
            <FaEnvelope />
          </IconWrapper>
          <StyledInput
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Email de l'utilisateur"
            $hasError={!!validationErrors.email}
            disabled={!!user} // Désactiver pour les utilisateurs existants
          />
        </InputWrapper>
        {validationErrors.email && <ErrorMessage>{validationErrors.email}</ErrorMessage>}
      </FormGroup>
      
      {!user && (
        <>
          <FormGroup>
            <Label htmlFor="password">Mot de passe</Label>
            <InputWrapper>
              <IconWrapper>
                <FaLock />
              </IconWrapper>
              <StyledInput
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Mot de passe"
                $hasError={!!validationErrors.password}
              />
            </InputWrapper>
            {validationErrors.password && <ErrorMessage>{validationErrors.password}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
            <InputWrapper>
              <IconWrapper>
                <FaLock />
              </IconWrapper>
              <StyledInput
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirmer le mot de passe"
                $hasError={!!validationErrors.confirmPassword}
              />
            </InputWrapper>
            {validationErrors.confirmPassword && <ErrorMessage>{validationErrors.confirmPassword}</ErrorMessage>}
          </FormGroup>
        </>
      )}
      
      <FormGroup>
        <Label htmlFor="firstName">Prénom</Label>
        <InputWrapper>
          <IconWrapper>
            <FaUser />
          </IconWrapper>
          <StyledInput
            id="firstName"
            name="firstName"
            type="text"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="Prénom de l'utilisateur"
            $hasError={!!validationErrors.firstName}
          />
        </InputWrapper>
        {validationErrors.firstName && <ErrorMessage>{validationErrors.firstName}</ErrorMessage>}
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="name">Nom</Label>
        <InputWrapper>
          <IconWrapper>
            <FaUser />
          </IconWrapper>
          <StyledInput
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="Nom de l'utilisateur"
            $hasError={!!validationErrors.name}
          />
        </InputWrapper>
        {validationErrors.name && <ErrorMessage>{validationErrors.name}</ErrorMessage>}
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="role">Rôle</Label>
        <InputWrapper>
          <IconWrapper>
            <FaUserTag />
          </IconWrapper>
          <StyledInput
            id="role"
            name="role"
            type="text"
            value="Utilisateur"
            disabled
            placeholder="Utilisateur"
          />
        </InputWrapper>
      </FormGroup>
      

      
      {success && (
        <SuccessMessage>
          {user ? 'Utilisateur mis à jour avec succès !' : 'Utilisateur créé avec succès !'}
        </SuccessMessage>
      )}
      
      <ButtonsContainer>
        <Button 
          type="button" 
          onClick={onCancel} 
          $variant="secondary"
          disabled={loading}
        >
          Annuler
        </Button>
        <Button 
          type="submit" 
          $variant="primary"
          disabled={loading}
          onClick={(e) => {
            // Double vérification des mots de passe avant soumission
            if (!user && formData.password !== formData.confirmPassword) {
              e.preventDefault();
              setValidationErrors(prev => ({
                ...prev,
                password: "Les mots de passe ne correspondent pas",
                confirmPassword: "Les mots de passe ne correspondent pas"
              }));
              addNotification({
                type: 'warning',
                title: 'Erreur',
                message: 'Les mots de passe ne correspondent pas'
              });
              return;
            }
          }}
        >
          {loading ? 'Chargement...' : user ? 'Mettre à jour' : 'Ajouter'}
        </Button>
      </ButtonsContainer>
    </FormContainer>
  );
};

export default UserForm;

